import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Chip,
  Button,
} from "@mui/material";
import React from "react";
import { languagesList } from "../../../utils/data";
import { LanguageLevelDropdown } from "../shared/LanguageLevelDropdown";
import AddIcon from "@mui/icons-material/Add";
import Language from "../../../../types/Language";
import CustomAutocomplete from "../../../shared/CustomAutocomplete";

interface LanguageSectionProps {
  setLanguages: React.Dispatch<React.SetStateAction<Language>>;
  languages: Language;
  handleDeleteLanguage: (index: number) => void;
  selectedLanguage: string;
  setSelectedLanguage: React.Dispatch<React.SetStateAction<string>>;
  selectedLevel: string;
  setSelectedLevel: React.Dispatch<React.SetStateAction<string>>;
  handleAddLanguage: () => void;
  errors: Record<string, string>;
  removeErrorMessage: (field: string) => void;
}

export const LanguageSection: React.FC<LanguageSectionProps> = ({
  setLanguages,
  languages,
  handleDeleteLanguage,
  selectedLanguage,
  setSelectedLanguage,
  selectedLevel,
  setSelectedLevel,
  handleAddLanguage,
  errors,
  removeErrorMessage,
}) => {
  return (
    <Box>
      <Typography variant="h4">Languages</Typography>
      <Box sx={{ border: "1px solid black" }}></Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          marginTop: "1rem",
        }}
      >
        <CustomAutocomplete
          label="Native Language"
          value={languages.nativeLanguage}
          options={languagesList}
          onChange={(newValue) => {
            setLanguages((prev) => ({
              ...prev,
              nativeLanguage: newValue as string,
            }));
            removeErrorMessage("nativeLanguage");
          }}
          multiple={false}
          error={!!errors.nativeLanguage}
          errorMessage={errors.nativeLanguage}
          removeErrorMessage={removeErrorMessage}
          fieldName="nativeLanguage"
        />
        <FormControl sx={{ marginTop: "1rem" }}>
          <FormLabel htmlFor="english">English</FormLabel>
          <Select
            value={languages.english}
            error={!!errors.english}
            onChange={(e) => {
              setLanguages((prevLanguages) => ({
                ...prevLanguages,
                english: e.target.value as string,
              }));
              removeErrorMessage("english");
            }}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ opacity: 0.6 }}>Select a level</span>;
              }
              return selected;
            }}
            sx={{
              height: "50px",
              background: "none",
              "& .MuiSelect-select": {
                height: "32px",
              },
            }}
          >
            <MenuItem value="Basic">Basic</MenuItem>
            <MenuItem value="Advanced">Advanced</MenuItem>
          </Select>
          {errors.english && (
            <Typography fontSize={"12px"} color="error" marginTop={"3px"}>
              {errors.english}
            </Typography>
          )}
        </FormControl>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: "1rem",
          }}
        >
          <FormLabel htmlFor="otherLanguages">Other Languages</FormLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {languages.otherLanguages.map((lang, index) => (
              <Chip
                key={index}
                label={`${lang.name} - ${lang.level}`}
                onDelete={() => handleDeleteLanguage(index)}
                sx={{ marginBottom: "8px" }}
              />
            ))}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "row", gap: "2rem" }}>
            <Select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              sx={{
                background: "none",
                "& .MuiSelect-select": {
                  height: "32px",
                },
              }}
            >
              {languagesList.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang}
                </MenuItem>
              ))}
            </Select>
            <LanguageLevelDropdown
              value={selectedLevel}
              setSelectedLevel={setSelectedLevel}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddLanguage}
              sx={{
                height: "50px",
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
